import type { RaRecord } from "ra-core";
import { useInfinitePaginationContext, useListContext } from "ra-core";
import type { ReactNode } from "react";
import {
  VirtuosoGrid,
  type GridStateSnapshot,
  type VirtuosoGridHandle,
} from "react-virtuoso";

import { cn } from "@/lib/utils";

// react-virtuoso omits getState from VirtuosoGridHandle, but the grid implements it:
// its runtime method map exposes getState alongside scrollTo and scrollBy, and the
// package exports GridStateSnapshot, which only the grid can produce. Declaring it here
// rather than casting keeps the scroll-restoration hook honestly typed. Drop this once
// upstream adds the method to its own type.
type GridHandleWithState = VirtuosoGridHandle & {
  getState(cb: (snapshot: GridStateSnapshot) => void): void;
};

import {
  LoadingFooter,
  useSyncRadixThumbHeightFromGrid,
  useVirtuosoScrollRestoration,
  VirtuosoScroller,
} from "./VirtualizedListContent";

/**
 * Virtualized card-grid body for infinite lists — the grid twin of
 * VirtualizedListContent. Same contract: rows come from ListContext, paging
 * from InfinitePaginationContext (via <InfiniteList>); scrolling happens in
 * the themed <ScrollArea> via the shared VirtuosoScroller override (see its
 * docs for the hidden-tab rationale). VirtuosoGrid assumes uniformly sized
 * items.
 */
export const VirtualizedGridContent = <RecordType extends RaRecord = RaRecord>({
  itemContent,
  // Horizontal padding only: VirtuosoGrid overwrites the list element's
  // vertical padding with inline styles (that's its windowing mechanism), so
  // top spacing comes from the Header spacer below instead.
  listClassName = "grid gap-2 px-4 grid-cols-[repeat(auto-fill,minmax(180px,1fr))]",
  className,
}: VirtualizedGridContentProps<RecordType>) => {
  const { data, isPending } = useListContext<RecordType>();
  const { fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfinitePaginationContext();
  const { scrollerRef } = useSyncRadixThumbHeightFromGrid();
  const { virtuosoRef, restoreStateFrom, handleIsScrolling } =
    useVirtuosoScrollRestoration<GridHandleWithState>();

  if (!fetchNextPage) {
    throw new Error(
      "VirtualizedGridContent must be used inside an InfinitePaginationContext, usually created by <InfiniteList>. You cannot use it as child of a <List> component.",
    );
  }

  if (isPending || !data) return null;

  return (
    <VirtuosoGrid
      ref={virtuosoRef}
      restoreStateFrom={restoreStateFrom as GridStateSnapshot | undefined}
      isScrolling={handleIsScrolling}
      className={cn("flex-1 min-h-0", className)}
      style={{ height: "100%" }}
      data={data}
      computeItemKey={(_, record) => record.id}
      endReached={() => {
        if (hasNextPage && !isFetchingNextPage) fetchNextPage();
      }}
      listClassName={listClassName}
      components={{
        Scroller: VirtuosoScroller,
        Header: GridTopSpacer,
        Footer: LoadingFooter,
      }}
      itemContent={itemContent}
      scrollerRef={scrollerRef}
    />
  );
};

/** Top gap between the panel edge and the first card row (Sam's request). */
const GridTopSpacer = () => <div aria-hidden className="h-4" />;

export interface VirtualizedGridContentProps<
  RecordType extends RaRecord = RaRecord,
> {
  /** Cell renderer — receives the index and the record. */
  itemContent: (index: number, record: RecordType) => ReactNode;
  /** Classes for the grid container element (lays out the cells). */
  listClassName?: string;
  className?: string;
}
